/**
 * Contratos y, sobre todo, **el reparto**.
 *
 * El reparto es el corazón del sistema: cambiarlo no edita la versión vigente,
 * abre una nueva desde una fecha. Por eso agregar un médico en el mes 9 no
 * reordena los meses ya cobrados, y la pantalla lo dice en voz alta.
 */

import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/components/common/NavIcons';
import {
  ALLOCATION_MODE_LABEL, dateLabel, SUBSCRIPTION_STATUS_LABEL, SUBSCRIPTION_STATUS_TONE,
  type AllocationMode, type SubscriptionStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { DistributionEditor } from './Distribution';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';

interface Contract {
  id: string; service_unit_id: string; unit_code: string; unit_name: string | null; service_name: string;
  status: SubscriptionStatus; start_date: string; end_date: string | null;
  base_price_minor: number; price_override_minor: number | null;
  discount_type: string; discount_percent: number;
  allocation_id: string | null; allocation_mode: AllocationMode | null;
  allocation_confirmed: number; payer_count: number;
}

export function ContractsPage() {
  const query = useApi<{ subscriptions: Contract[] }>('/admin/subscriptions');

  return (
    <>
      <PageHead
        title="Paquetes"
        subtitle="Consultorio + servicio + precio + reparto. Sin reparto no se generan cobros."
      />

      <Async query={query} empty={(d) => d.subscriptions.length === 0 && (
        <Empty icon="lista" title="Todavía no hay paquetes" hint="Un consultorio sin paquete es un prospecto." />
      )}>
        {(data) => {
          const unconfirmed = data.subscriptions.filter((s) => !s.allocation_confirmed && s.allocation_id);
          return (
            <>
              {unconfirmed.length > 0 && (
                <Note tone="warn">
                  <strong>{unconfirmed.length}</strong> repartos nunca se confirmaron contra la
                  facturación real: se está asumiendo partes iguales. Los cobros salen, pero pueden
                  estar repartidos mal.
                </Note>
              )}

              <Card flush>
                <Table head={
                  <tr>
                    <th>Consultorio</th><th>Servicio</th><th className="right">Precio</th>
                    <th>Reparto</th><th>Estado</th>
                  </tr>
                }>
                  {data.subscriptions.map((s) => (
                    <tr key={s.id}>
                      <td className="strong">
                        <Link to={`/admin/contratos/${s.id}`}>
                          <Cell sub={s.unit_name}>{s.unit_code}</Cell>
                        </Link>
                      </td>
                      <td>
                        <Cell sub={s.discount_type === 'PERCENTAGE' ? `${s.discount_percent}% de descuento` : undefined}>
                          {s.service_name}
                        </Cell>
                      </td>
                      <td className="right">
                        <Money value={s.price_override_minor ?? s.base_price_minor} />
                      </td>
                      <td>
                        {s.allocation_mode ? (
                          <>
                            <Badge tone={s.allocation_confirmed ? 'ok' : 'warn'}>
                              {ALLOCATION_MODE_LABEL[s.allocation_mode]}
                            </Badge>
                            <span className="tbl__sub">
                              {s.payer_count} pagador{s.payer_count === 1 ? '' : 'es'}
                              {!s.allocation_confirmed && ' · sin confirmar'}
                            </span>
                          </>
                        ) : <Badge tone="danger">Sin definir</Badge>}
                      </td>
                      <td>
                        <StatusBadge
                          value={s.status}
                          labels={SUBSCRIPTION_STATUS_LABEL}
                          tones={SUBSCRIPTION_STATUS_TONE}
                        />
                      </td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </>
          );
        }}
      </Async>
    </>
  );
}

/* ── Detalle ──────────────────────────────────────────────────────────────── */

interface Allocation {
  id: string; mode: AllocationMode; effective_from: string; effective_to: string | null;
  rotation_anchor: string | null; confirmed: number; notes: string | null;
}

interface Member {
  id: string; allocation_id: string; party_id: string; ordinal: number;
  percentage: number | null; fixed_amount_minor: number | null; rotation_seq: number | null;
  display_name: string; legal_name: string | null; tax_id: string | null;
}

interface ContractDetail {
  subscription: Contract & {
    notes: string | null; tax_rate: number; due_days: number;
    service_description: string | null; service_features_json: string;
  };
  allocations: Allocation[];
  members: Member[];
}

/**
 * Lo que diferencia un Paquete Regular de un Premium.
 *
 * Hoy esa diferencia no está cargada en ningún lado más que el precio — no se
 * inventa contenido acá. El campo ya existe en `services.features_json`
 * (se define desde Catálogo); esta card solo lo muestra, con un vacío
 * explícito en vez de fingir que no hay nada que decir.
 */
function PackageFeatures({ description, featuresJson }: { description: string | null; featuresJson: string }) {
  let features: string[] = [];
  try {
    const parsed = JSON.parse(featuresJson || '[]');
    if (Array.isArray(parsed)) features = parsed.filter((f): f is string => typeof f === 'string');
  } catch {
    features = [];
  }

  return (
    <Card title="Características del paquete">
      {description && <p className="small muted mb-2">{description}</p>}
      {features.length === 0 ? (
        <p className="muted small">
          Sin características definidas todavía. Se cargan desde Catálogo.
        </p>
      ) : (
        <ul className="feature-list">
          {features.map((f) => <li key={f}>{f}</li>)}
        </ul>
      )}
    </Card>
  );
}

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useApi<ContractDetail>(`/admin/subscriptions/${id}`);

  const activate = useApiMutation(
    () => api.post(`/admin/subscriptions/${id}/status`, { status: 'ACTIVE' }),
    ['/admin/subscriptions'],
  );

  return (
    <Async query={query}>
      {({ subscription: s, allocations, members }) => {
        const current = allocations.find((a) => !a.effective_to) ?? allocations[0];

        return (
          <>
            <PageHead
              title={`${s.unit_code} · ${s.service_name}`}
              subtitle={
                <>
                  Desde {dateLabel(s.start_date)}
                  {' · '}
                  <StatusBadge
                    value={s.status}
                    labels={SUBSCRIPTION_STATUS_LABEL}
                    tones={SUBSCRIPTION_STATUS_TONE}
                  />
                </>
              }
              actions={
                <>
                  {s.status === 'DRAFT' && current && (
                    <Button variant="primary" disabled={activate.isPending} onClick={() => activate.mutate()}>
                      Activar paquete
                    </Button>
                  )}
                  <Link to="/admin/contratos" className="btn" title="Volver" aria-label="Volver a Paquetes"><Icon name="volver" /></Link>
                </>
              }
            />

            {activate.error && <Note tone="danger">{activate.error.message}</Note>}

            <div className="grid grid--sidebar">
              <div style={{ display: 'grid', gap: 14 }}>
                {/* El editor visual fusiona la regla vigente (antes acá mismo,
                    en una card aparte) y la rejilla anual — un solo lugar
                    para "quién paga", en vez de dos pantallas separadas. */}
                <DistributionEditor subscriptionId={s.id} />

                {allocations.length > 1 && (
                  <Card title="Versiones anteriores" flush>
                    <Table head={<tr><th>Modo</th><th>Vigencia</th><th>Pagadores</th></tr>}>
                      {allocations.filter((a) => a.id !== current?.id).map((a) => (
                        <tr key={a.id} style={{ opacity: 0.7 }}>
                          <td>{ALLOCATION_MODE_LABEL[a.mode]}</td>
                          <td className="small muted">
                            {dateLabel(a.effective_from)} → {a.effective_to ? dateLabel(a.effective_to) : 'hoy'}
                          </td>
                          <td>{members.filter((m) => m.allocation_id === a.id).length}</td>
                        </tr>
                      ))}
                    </Table>
                  </Card>
                )}
              </div>

              <Card title="Condiciones">
                <dl className="dl">
                  <dt>Precio</dt>
                  <dd><Money value={s.price_override_minor ?? s.base_price_minor} /></dd>
                  <dt>Descuento</dt>
                  <dd>{s.discount_type === 'PERCENTAGE' ? `${s.discount_percent}%` : '—'}</dd>
                  <dt>IVA</dt><dd>{(s.tax_rate * 100).toFixed(0)}%</dd>
                  <dt>Pago ordinario</dt><dd>Del día 1 al 5</dd>
                  <dt>Atrasado desde</dt><dd>Día 6</dd>
                  <dt>Inicio</dt><dd>{dateLabel(s.start_date)}</dd>
                  <dt>Fin</dt><dd>{s.end_date ? dateLabel(s.end_date) : '—'}</dd>
                </dl>
              </Card>

              <PackageFeatures description={s.service_description} featuresJson={s.service_features_json} />
            </div>
          </>
        );
      }}
    </Async>
  );
}
